import { useEffect, useState } from "react";
import { Card, Box } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import Typography from "@mui/material/Typography"
import axiosInstance from '../../axios.config';

export const PassPercentageBlock = ({ courseId }) => {

    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [courseName, setCourseName] = useState("");
    const [assignedCount, setAssignedCount] = useState(0);

    useEffect(() => {
        setChartData(null);

        const fetchData = async () => {
            if (!courseId) return;

            setLoading(true); // ✅ START loading

            try {
                const response = await axiosInstance.get(
                    `/UserCourseStep/GetAllCoursesAssignedCompleted/${courseId}`
                );

                const data = response.data;
                setCourseName(data.courseName);
                setAssignedCount(data.assignedCount);
                setChartData([
                    { id: 'Completed', label: 'Completed', value: data.completedCount, color: '#4caf50' },
                    { id: 'InProgress', label: 'In Progress', value: data.inProgressCount, color: '#ff9800' },
                    { id: 'NotStarted', label: 'Not Started', value: data.notStartedCount, color: '#f44336' },
                ]);

            } catch (error) {
                console.error(error);
                setChartData([]);
            } finally {
                setLoading(false); // ✅ ALWAYS stop loading
            }
        };

        fetchData();
    }, [courseId]);

    // ---------------- UI ----------------

    if (!courseId) {
        return (
            <Card sx={{ height: 460, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography>Select a course to view progress</Typography>
            </Card>
        );
    }

    if (loading) {
        return (
            <Card sx={{ height: 460, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography>Loading...</Typography>
            </Card>
        );
    }

    if (!chartData || chartData.length === 0) {
        return (
            <Card sx={{ height: 460, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography>No data available</Typography>
            </Card>
        );
    }

    return (
        <Card sx={{ height: 460, display: "flex", flexDirection: "column"}}>
            <Box sx={{ height: '100%', overflow: 'visible' }} >
                <Typography variant="h6" fontWeight={600} textAlign="center"
                    sx={{
                        mb: 1,
                        textOverflow: "ellipsis",
                        overflow: "hidden"
                    }}>
                    {courseName ? `${courseName} Progress` : "Course Progress"}
                </Typography>

                <Box mt={2} display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="300px">
                    <PieChart
                        series={[
                            {
                                data: chartData,
                                innerRadius: 40,
                                outerRadius: 100,
                            },
                        ]}
                        tooltip={{
                            trigger: 'item',
                            formatter: ({ label, value }) => `${label}: ${value}`,
                        }}
                        width={260}
                        height={200}
                        slotProps={{
                            legend: { hidden: true } // ✅ hide built-in legend
                        }}
                        margin={{ right: -2 }}
                    />
                    {/* ✅ custom legend below */}
                    <Box mt={3} width="100%">

                        {/* Row 1: Labels */}
                        <Box display="flex" justifyContent="space-around" textAlign="center">
                            <Box>
                                <Typography variant="body2" fontWeight={600}>
                                    Assigned To
                                </Typography>
                            </Box>

                            {chartData.map((item) => (
                                <Box key={item.id}>
                                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                backgroundColor: item.color,
                                                borderRadius: "50%"
                                            }}
                                        />
                                        <Typography variant="body2" fontWeight={600}>
                                            {item.label}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        {/* Row 2: Values */}
                        <Box display="flex" justifyContent="space-around" textAlign="center" mt={1}>
                            <Typography variant="body2">{assignedCount}</Typography>

                            {chartData.map((item) => (
                                <Typography key={item.id} variant="body2">
                                    {item.value}
                                </Typography>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Card>
    );
};